import React, { useEffect, useRef } from 'react';
import { Spinner } from '@nextui-org/react';
import { t } from '../i18n';

/**
 * Endlos-Scrollen (2026-09-02, Marcel): lädt automatisch nach, sobald der
 * Sentinel in den Viewport kommt (IntersectionObserver, 400px Vorlauf).
 * Fallback ohne IntersectionObserver (alte Browser, jsdom): klassischer
 * „Mehr laden“-Button. Wiederverwendbar für alle Listen mit Offset-Paging.
 */
interface InfiniteScrollSentinelProps {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  /** Text während des Nachladens, z. B. „Lade weitere Spieler …“ */
  loadingLabel?: string;
  /** Fallback-Button-Text (ohne IntersectionObserver) */
  fallbackLabel?: string;
}

const InfiniteScrollSentinel: React.FC<InfiniteScrollSentinelProps> = ({
  hasMore, loading, onLoadMore, loadingLabel = t('web.component.infinite-scroll-sentinel.lade-weitere'), fallbackLabel = t('web.component.comment-section.mehr-laden'),
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const supported = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    if (!supported || !hasMore || loading) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    // Observer wird nach jedem abgeschlossenen Ladevorgang neu angelegt (loading in den Deps):
    // der erste Callback meldet den aktuellen Zustand → steht der Sentinel noch im
    // Vorlauf, lädt es sofort weiter, auch ohne neues Scroll-Ereignis (2026-09-24).
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !loadingRef.current) onLoadMore();
    }, { rootMargin: '400px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [supported, hasMore, loading, onLoadMore]);

  if (!hasMore) return null;
  return (
    // Spinner NUR während des Ladens (Marcel 2026-09-24), sonst unsichtbarer Auslöser
    <div ref={ref} className="flex min-h-[48px] justify-center py-4" data-testid="infinite-scroll-sentinel">
      {loading ? (
        <span className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" color="primary" /> {loadingLabel}
        </span>
      ) : !supported ? (
        <button type="button" onClick={onLoadMore} className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-gray-300 hover:border-[#3fced1]">
          {fallbackLabel}
        </button>
      ) : null}
    </div>
  );
};

export default InfiniteScrollSentinel;
